import { useLibrary } from '@/hooks/library';
import { Button, Flex, Modal, Table, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { LibraryContext } from './context-provider';
import NewFolderModal from './modal-new-folder';
const { Text, Title } = Typography;

export default function MoveModal({ open, setOpen }) {
   const [newFolderOpen, setNewFolderOpen] = useState(false);
   const { selectedLibrary, library } = useContext(LibraryContext);
   const [folder, setFolder] = useState(null);
   const [selected, setSelected] = useState(null);
   const [disabled, setDisabled] = useState(true);
   // get folder content using swr hook to get static data from getFolder function
   const folderContent = useLibrary(folder?._id);

   const columns = [
      {
         title: 'Name',
         dataIndex: 'name',
         key: 'name',
         render: (text, record) => (
            <Flex gap={16} align="center">
               <i className="fi fi-sr-folder" style={{ fontSize: 16, color: record.color }} />
               <a
                  onClick={() => {
                     setFolder(record);
                  }}
               >
                  {text}
               </a>
            </Flex>
         ),
      },
      {
         title: 'Created At',
         dataIndex: 'createdAt',
         key: 'createdAt',
         render: (text) => <Text type="secondary">{moment(text).format('DD MMM YYYY')}</Text>,
      },
   ];

   useEffect(() => {
      if (selectedLibrary) {
         setFolder({ _id: selectedLibrary[0]?.parentFolder });
      }
   }, [selectedLibrary]);

   // disable move button if no folder selected or selected folder is the same as the current folder
   useEffect(() => {
      // if no folder selected
      if (!selected) {
         setDisabled(true);
      }
      // if active selections contains selected folder
      else if (selectedLibrary?.find((item) => item._id === selected?._id)) {
         setDisabled(true);
      } else {
         setDisabled(false);
      }
   }, [selected, selectedLibrary]);

   async function moveFile() {
      // loop through selectedLibrary and update each file
      for (const item of selectedLibrary) {
         if (item.type === 'file') {
            await library.moveFile(item._id, selected?._id);
         }
         if (item.type === 'folder') {
            await library.moveFolder(item._id, selected?._id);
         }
      }
      setOpen(false);
   }

   return (
      <>
         <NewFolderModal
            open={newFolderOpen}
            setOpen={setNewFolderOpen}
            library={folderContent}
            parentFolder={folder?._id}
         />
         <Modal
            open={open}
            onClose={() => setOpen(false)}
            width={800}
            onCancel={() => setOpen(false)}
            closable={false}
            okText="Move"
            okButtonProps={{ disabled: disabled }}
            onOk={moveFile}
            destroyOnClose={true}
         >
            <Flex vertical gap={32}>
               <Flex justify="space-between" align="center">
                  <Title level={4}>Move to</Title>
                  <Tooltip title="Create Folder">
                     <Button
                        type="text"
                        icon={<i className="fi fi-rr-add-folder" />}
                        onClick={() => setNewFolderOpen(true)}
                     />
                  </Tooltip>
               </Flex>
               <Flex align="center" gap={8}>
                  <Button type="text" onClick={() => setFolder(null)} size="small">
                     My Library
                  </Button>
                  <i className="fi fi-rr-angle-small-right" />
                  {folderContent?.data?.parentFolderPath?.map((item) => (
                     <>
                        <Button type="text" onClick={() => setFolder(item)} size="small">
                           {item.name}
                        </Button>
                        <i className="fi fi-rr-angle-small-right" />
                     </>
                  ))}
                  <Text>{folder?.name}</Text>
               </Flex>

               <Table
                  dataSource={folderContent?.data?.folders?.filter(
                     (folder) => !selectedLibrary?.some((item) => item._id === folder._id)
                  )}
                  columns={columns}
                  rowKey="_id"
                  loading={library.isLoading || folderContent?.isLoading}
                  pagination={false}
                  size="small"
                  onRow={(record) => {
                     return {
                        onClick: () => {
                           setSelected(record);
                        },
                        onDoubleClick: () => {
                           setFolder(record);
                        },
                        style: { cursor: 'pointer' },
                     };
                  }}
                  rowSelection={{
                     type: 'radio',
                     selectedRowKeys: [selected?._id],
                     onChange: (selectedRowKeys, selectedRows) => {
                        setSelected(selectedRows[0]);
                     },
                  }}
               />
            </Flex>
         </Modal>
      </>
   );
}
